import { Alert, Button, Stack } from '@mui/material';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useGetServiceProviderPhoenix } from '@pflegenavi/service-provider-frontend/api';

/**
 * Dashboard banner prompting service providers to complete MangoPay KYC
 * verification while it is still pending (i.e. their documents have not been
 * validated yet).
 *
 * The profile KYC section is only reachable once a bank account is set up
 * (until then, redirect logic bounces the user back to bank account setup),
 * so we only surface this banner once the bank account is configured —
 * keeping the "view verification status" link always reachable.
 */
export const KycVerificationBanner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: serviceProvider } = useGetServiceProviderPhoenix();

  const handleGoToProfile = useCallback(() => {
    navigate(spAppMainPages.PROFILE);
  }, [navigate]);

  const showBanner =
    serviceProvider?.data?.payment_service_provider === 'mangopay' &&
    serviceProvider?.data?.mangopay_user_id &&
    serviceProvider?.data?.mangopay_bank_account_id &&
    !serviceProvider?.data?.mangopay_kyc_verified;

  if (!showBanner) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleGoToProfile}
          >
            {t('service-provider-app.dashboard.complete-kyc-button')}
          </Button>
        </Stack>
      }
    >
      {t('service-provider-app.dashboard.kyc-verification-required')}
    </Alert>
  );
};
